import { ref } from 'vue';

/**
 * Set the moment any call comes back 401. Nothing navigates away on its own any more — App.vue
 * watches this and swaps in the login page, so an expired session leaves you looking at a
 * "Sign in with Google" button rather than being bounced straight into Google's consent screen
 * mid-task. Sign-in only starts when someone actually presses that button (a navigation to
 * /auth/login, which is what issues the OAuth challenge).
 */
export const unauthenticated = ref(false);

/** Thrown on a 401 so callers can tell "your session is gone" apart from a real failure. */
export class UnauthorizedError extends Error {
  constructor() {
    super('Not signed in.');

    this.name = 'UnauthorizedError';
  }
}

/**
 * Any other non-2xx. Still an Error whose message is the response body, so every existing
 * `(e as Error).message` keeps working; `status` is there for the few callers that must react to
 * one code specifically (e.g. a stale events cursor answering 400).
 */
export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);

    this.name = 'HttpError';
  }
}

/**
 * Every call rides the browser's own AdminCookie session — nothing here ever holds a credential
 * of its own. A non-2xx response's body is plain text (ASP.NET Core's ProblemDetails "detail",
 * or a plain string), so it's surfaced as-is via Error.message.
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) }
  });

  if (response.status === 401) {
    // Cookie auth's OnRedirectToLogin (see Program.cs) hands /api/* a plain 401 rather than a
    // redirect precisely so this can be handled in-app: a fetch() that followed the redirect would
    // just dead-end on Google's CORS-blocked OAuth page.
    unauthenticated.value = true;

    throw new UnauthorizedError();
  }

  if (!response.ok) {
    throw new HttpError((await response.text()) || `${response.status} ${response.statusText}`, response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * The human-readable part of a failed call: when the thrown message is a ProblemDetails JSON body
 * (what `Problem(...)` actually sends, as application/problem+json), its `detail` (else `title`);
 * otherwise the message unchanged. Never throws — anything unparsable is returned as-is.
 */
export function errorDetail(e: unknown): string {
  const message = e instanceof Error ? e.message : String(e);
  try {
    const parsed: unknown = JSON.parse(message);
    if (parsed && typeof parsed === 'object') {
      const { detail, title } = parsed as { detail?: unknown; title?: unknown };
      if (typeof detail === 'string' && detail) return detail;
      if (typeof title === 'string' && title) return title;
    }
  } catch {
    // Not JSON — a plain-text body or a network error message.
  }
  return message;
}

export const post = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const patch = <T>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export const del = <T>(path: string): Promise<T> => api<T>(path, { method: 'DELETE' });
